// src/components/InvoicesHubPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";

// Reuse existing styles from payroll tabs & invoice popups
import "./ViewPayroll.css";
import "./InvoiceReferencePage.css";

// Child tabs
import InvoiceReferencePage from "./InvoiceReferencePage";
import InvoicePage from "./InvoicePage"; // settings
import EstimateReferencePage from "./EstimateReferencePage";

/* ======================================================
   SVG TAB DEFS + COMPONENT (same as ViewPayroll)
   ====================================================== */
function TabSvgDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      <defs>
        <filter id="tabShadow">
          <feComponentTransfer in="SourceGraphic">
            <feFuncR type="discrete" tableValues="0" />
            <feFuncG type="discrete" tableValues="0" />
            <feFuncB type="discrete" tableValues="0" />
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="1" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.2" />
          </feComponentTransfer>
          <feOffset dx="5" dy="1" result="shadow" />
          <feComposite in="SourceGraphic" />
        </filter>

        {/* Inactive gradient (blue-grey like the mock) */}
        <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#E5ECF6" />
          <stop offset="100%" stopColor="#D7E0ED" />
        </linearGradient>

        {/* Active gradient (white) */}
        <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#FAFBFC" />
        </linearGradient>

        {/* Tab shape with left inward curve + right downward slope */}
        <symbol id="tab-shape" viewBox="0 0 122 40">
          <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
        </symbol>
      </defs>
    </svg>
  );
}

function SvgTabs({ tabs, activeIndex, onChange }) {
  return (
    <nav className="svg-tabs" role="navigation" aria-label="Invoice sections">
      <ul className="svg-tablist">
        {tabs.map((label, i) => {
          const active = i === activeIndex;
          return (
            <li key={label} className={`svg-tab ${active ? "is-active" : ""}`}>
              <button
                type="button"
                className="svg-tab-link"
                onClick={() => onChange(i)}
                aria-selected={active}
                aria-controls={`panel-${i}`}
                role="tab"
              >
                <span className="svg-tab-label">{label}</span>
                <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
                  <use xlinkHref="#tab-shape" />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ======================================================
   HUB PAGE
   ====================================================== */
export default function InvoicesHubPage() {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  /** -------- Tabs -------- */
  const TAB_KEYS = ["list", "estimates", "settings"];
  const TAB_LABELS = ["Invoices", "Estimates", "Settings"];
  const [activeTab, setActiveTab] = useState("list");
  const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
  const setActiveByIndex = (i) => setActiveTab(TAB_KEYS[i] ?? "list");

  /** -------- Data for tiles + selects -------- */
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await api.get(`/business/${businessId}/invoices`);
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed fetching invoices", e);
      setInvoices([]);
    }
  }, [businessId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    (async () => {
      try {
        const vendorRes = await api.get(`/business/${businessId}/vendors`);
        setVendors(Array.isArray(vendorRes.data) ? vendorRes.data : []);
      } catch (e) {
        console.error("Failed to load vendors", e);
      }
    })();
  }, [businessId]);

  // Allow children (lists) to ask hub to refresh tiles
  useEffect(() => {
    const onRefresh = () => fetchInvoices();
    window.addEventListener("invoices-refresh", onRefresh);
    return () => window.removeEventListener("invoices-refresh", onRefresh);
  }, [fetchInvoices]);

  const stats = useMemo(() => {
    const total = invoices.length;
    let paid = 0,
      pending = 0,
      overdue = 0;
    for (const inv of invoices) {
      const bal = Number(inv.total_balance ?? inv.balance ?? 0);
      const status = (inv.status || "").toLowerCase();
      if (status === "paid" || bal <= 0) paid++;
      else if (status === "overdue") overdue++;
      else pending++;
    }
    return { total, paid, pending, overdue };
  }, [invoices]);

  /** =================== CSV Helpers (shared) =================== */
 const parseCsvLine = (line) => {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

const cleanMoneyValue = (value) => {
  if (value === null || value === undefined) return "";

  let str = String(value).trim();

  // Handle parentheses as negative: ($1,234.56)
  const isParenthesesNegative = /^\(.*\)$/.test(str);

  str = str
    .replace(/^\((.*)\)$/, "$1")
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "");

  if (!str || str === "-" || str === ".") return "";

  const num = Number(str);
  if (Number.isNaN(num)) return "";

  return (isParenthesesNegative ? -Math.abs(num) : num).toFixed(2);
};

const parseCsv = (text) => {
  const lines = text
    .replace(/\r/g, "")
    .split("\n")
    .filter((l) => l.trim() !== "");

  if (!lines.length) return [];

  const normalizeKey = (key) => {
    switch (key.trim().toLowerCase()) {
      case "date":
        return "entry_date";
      case "description":
        return "description";
      case "amount":
        return "amount";
      default:
        return key.trim().toLowerCase();
    }
  };

  const headers = parseCsvLine(lines[0]).map(normalizeKey);

  return lines.slice(1).map((line) => {
    const values = parseCsvLine(line);

    return headers.reduce((obj, header, i) => {
      let value = values[i] || "";

      if (header === "amount") {
        value = cleanMoneyValue(value);
      }

      obj[header] = value;
      return obj;
    }, {});
  });
};

  /** =================== Invoice CSV Flow (in Hub) =================== */
  const invoiceFileRef = useRef(null);
  const [showInvoiceUploadInstruction, setShowInvoiceUploadInstruction] = useState(false);
  const [showInvoiceCsvPopup, setShowInvoiceCsvPopup] = useState(false);
  const [invoiceCsvUploadData, setInvoiceCsvUploadData] = useState([]);
  const [uploadInvoiceType, setUploadInvoiceType] = useState("one-time");
  const [uploadInvoiceVendor, setUploadInvoiceVendor] = useState("");
  const [uploadInvoiceDate, setUploadInvoiceDate] = useState("");

  const handleInvoiceCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setInvoiceCsvUploadData(parseCsv(String(ev.target?.result || "")));
      setShowInvoiceCsvPopup(true);
      if (invoiceFileRef.current) invoiceFileRef.current.value = "";
    };
    reader.readAsText(file);
  };

  const submitInvoiceCsv = async () => {
    if (!uploadInvoiceVendor || !uploadInvoiceType) {
      alert("Please select customer and invoice type.");
      return;
    }
    if (uploadInvoiceType === "one-time" && !uploadInvoiceDate) {
      alert("Invoice date is required for one-time invoice.");
      return;
    }
    try {
      await api.post(`/business/${businessId}/invoices/upload`, {
        invoice_type: uploadInvoiceType,
        vendor_id: uploadInvoiceVendor,
        invoice_date: uploadInvoiceType === "one-time" ? uploadInvoiceDate : null,
        entries: invoiceCsvUploadData,
      });
      alert("Upload successful");
      setShowInvoiceCsvPopup(false);
      setInvoiceCsvUploadData([]);
      setUploadInvoiceVendor("");
      setUploadInvoiceDate("");
      await fetchInvoices();
      // Ask list tab to refresh its rows, too
      window.dispatchEvent(new CustomEvent("invoices-refresh"));
    } catch (err) {
      console.error("Invoice upload failed", err);
      alert("Failed to upload invoice.");
    }
  };

  /** =================== Estimate CSV Flow (in Hub) =================== */
  const estimateFileRef = useRef(null);
  const [showEstimateUploadInstruction, setShowEstimateUploadInstruction] = useState(false);
  const [showEstimateCsvPopup, setShowEstimateCsvPopup] = useState(false);
  const [estimateCsvUploadData, setEstimateCsvUploadData] = useState([]);
  const [uploadEstimateType, setUploadEstimateType] = useState("one-time");
  const [uploadEstimateVendor, setUploadEstimateVendor] = useState("");
  const [uploadEstimateDate, setUploadEstimateDate] = useState("");

  const handleEstimateCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEstimateCsvUploadData(parseCsv(String(ev.target?.result || "")));
      setShowEstimateCsvPopup(true);
      if (estimateFileRef.current) estimateFileRef.current.value = "";
    };
    reader.readAsText(file);
  };



  const submitEstimateCsv = async () => {
    if (!uploadEstimateVendor || !uploadEstimateType) {
      alert("Please select customer and estimate type.");
      return;
    }
    if (uploadEstimateType === "one-time" && !uploadEstimateDate) {
      alert("Estimate date is required for one-time estimate.");
      return;
    }
    try {
      // Preferred dedicated endpoint; fallback to invoices/upload with is_estimate
      try {
        await api.post(`/business/${businessId}/estimates/upload`, {
          invoice_type: uploadEstimateType,
          vendor_id: uploadEstimateVendor,
          invoice_date: uploadEstimateType === "one-time" ? uploadEstimateDate : null,
          entries: estimateCsvUploadData,
        });
      } catch (_ignore) {
        await api.post(`/business/${businessId}/invoices/upload`, {
          is_estimate: 1,
          invoice_type: uploadEstimateType,
          vendor_id: uploadEstimateVendor,
          invoice_date: uploadEstimateType === "one-time" ? uploadEstimateDate : null,
          entries: estimateCsvUploadData,
        });
      }
      alert("Upload successful");
      setShowEstimateCsvPopup(false);
      setEstimateCsvUploadData([]);
      setUploadEstimateVendor("");
      setUploadEstimateDate("");
      // Ask estimates tab to refresh
      window.dispatchEvent(new CustomEvent("estimates-refresh"));
    } catch (err) {
      console.error("Estimate upload failed", err);
      alert("Failed to upload estimate.");
    }
  };

  /** =================== Render =================== */
  return (
    <div className="view-payroll-page">
      <TabSvgDefs />

      <h1 className="pw-page-title">Invoice</h1>



     <div className="flex-bar-inv">
          {/* Hidden file inputs */}
          <input
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            ref={invoiceFileRef}
            onChange={handleInvoiceCsvUpload}
          />
          <input
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            ref={estimateFileRef}
            onChange={handleEstimateCsvUpload}
          />

          <button className="avg-cogs-btn" onClick={() => navigate(`/dashboard/business/${businessId}/invoicecreate`)}
           >
    Add Invoice
  </button>

  <button className="avg-cogs-btn" onClick={() => navigate(`/dashboard/business/${businessId}/estimatecreate`)}
   >
        New Estimate
        </button>

          <button
            className="avg-cogs-btn"
            onClick={() => setShowInvoiceUploadInstruction(true)}
            
            
          >
            Upload Invoice
          </button>

          <button
            className="avg-cogs-btn"
            onClick={() => setShowEstimateUploadInstruction(true)}
            
          >
            Upload Estimate
          </button>
        </div>



      {/* ===== Tiles + Always-visible Upload Actions ===== */}
      <div className="pw-stats-grid invoice-grid">
        <div className="chart-card" >
          <div className="vp-chart-title">Total Invoices</div>
          <div className="stats-total">{stats.total}</div>
          <div className="subtext-percent">All invoices created</div>
        </div>
        <div className="chart-card" >
          <div className="vp-chart-title">Paid Invoices</div>
          <div className="stats-total">{stats.paid}</div>
          <div className="subtext-percent">Payments received</div>
        </div>
        <div className="chart-card" >
          <div className="vp-chart-title">Pending Invoices</div>
          <div className="stats-total">{stats.pending}</div>
          <div className="subtext-percent">Awaiting payment</div>
        </div>
        <div className="chart-card" >
          <div className="vp-chart-title">Overdue Invoices</div>
          <div className="stats-total">{stats.overdue}</div>
          <div className="subtext-percent">Past due</div>
        </div>
      </div>
        {/* Always-visible upload card with both flows */}
        
        
    

      {/* ===== SVG Tabs ===== */}
      <SvgTabs tabs={TAB_LABELS} activeIndex={activeTabIndex} onChange={setActiveByIndex} />

      {/* ===== Panels under tabs ===== */}
      <div className="svg-panels">
        {/* Invoice List */}
        <div
          id="panel-0"
          className={`pw-card tab-panel ${activeTab === "list" ? "is-active" : ""}`}
        >
          <InvoiceReferencePage />
        </div>


        {/* Invoices Setting */}
        <div
          id="panel-2"
          className={`pw-card tab-panel ${activeTab === "settings" ? "is-active" : ""}`}
        >
          <InvoicePage />
        </div>

        {/* Estimates */}
        <div
          id="panel-3"
          className={`pw-card tab-panel ${activeTab === "estimates" ? "is-active" : ""}`}
        >
          <EstimateReferencePage />
        </div>
      </div>

      {/* =================== INVOICE: Instruction Popup =================== */}
      {showInvoiceUploadInstruction && (
        <>
          <div
            className="overlay-invoice"
            onClick={() => setShowInvoiceUploadInstruction(false)}
          ></div>
          <div className="popup-invoice upload-instruction-popup">
            <h4>CSV Upload Instructions</h4>
            <h5 style={{ color: "red" }}>REMOVE ANY TOTAL ROWS</h5>

            <div className="sample-csv-instructions">
              <div className="arrow-left-msg">→ Format your CSV as shown</div>

              <div className="sample-table-wrapper">
                <div className="arrow-top-row">
                  <div className="arrow-top-msg">↓ Required column</div>
                  <div className="arrow-top-msg">↓ Required column</div>
                  <div className="arrow-top-msg">↓ Required column</div>
                </div>

                <table className="sample-csv-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>mm-dd-yyyy</td>
                      <td>Hourly Service Fee</td>
                      <td>450.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <button
              className="save-pop-up"
              onClick={() => {
                setShowInvoiceUploadInstruction(false);
                setTimeout(() => invoiceFileRef.current?.click(), 200);
              }}
            >
              Upload CSV
            </button>
            <button
              className="cancel-pop-up"
              onClick={() => setShowInvoiceUploadInstruction(false)}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* =================== ESTIMATE: Instruction Popup =================== */}
      {showEstimateUploadInstruction && (
        <>
          <div
            className="overlay-invoice"
            onClick={() => setShowEstimateUploadInstruction(false)}
          ></div>
          <div className="popup-invoice upload-instruction-popup">
            <h4>CSV Upload Instructions</h4>
            <h5 style={{ color: "red" }}>REMOVE ANY TOTAL ROWS</h5>

            <div className="sample-csv-instructions">
              <div className="arrow-left-msg">→ Format your CSV as shown</div>

              <div className="sample-table-wrapper">
                <div className="arrow-top-row">
                  <div className="arrow-top-msg">↓ Required column</div>
                  <div className="arrow-top-msg">↓ Required column</div>
                  <div className="arrow-top-msg">↓ Required column</div>
                </div>

                <table className="sample-csv-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>mm-dd-yyyy</td>
                      <td>Design Service</td>
                      <td>450.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <button
              className="save-pop-up"
              onClick={() => {
                setShowEstimateUploadInstruction(false);
                setTimeout(() => estimateFileRef.current?.click(), 200);
              }}
            >
              Upload CSV
            </button>
            <button
              className="cancel-pop-up"
              onClick={() => setShowEstimateUploadInstruction(false)}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* =================== INVOICE: CSV Preview + Submit =================== */}
      {showInvoiceCsvPopup && (
        <>
          <div
            className="overlay-invoice"
            onClick={() => setShowInvoiceCsvPopup(false)}
          ></div>
          <div className="popup-transactions">
            <h4>Upload Invoice CSV</h4>

            <select
              value={uploadInvoiceVendor}
              onChange={(e) => setUploadInvoiceVendor(e.target.value)}
            >
              <option value="">Select Customer</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>

            <select
              value={uploadInvoiceType}
              onChange={(e) => setUploadInvoiceType(e.target.value)}
            >
              <option value="one-time">One Time</option>
              <option value="open">Open</option>
            </select>

            {uploadInvoiceType === "one-time" && (
              <input
                type="date"
                value={uploadInvoiceDate}
                onChange={(e) => setUploadInvoiceDate(e.target.value)}
              />
            )}

            {invoiceCsvUploadData.length > 0 && (
              <>
                <table className="table transactions-table" style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      {Object.keys(invoiceCsvUploadData[0] || {}).map((k) => (
                        <th key={k}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceCsvUploadData.slice(0, 10).map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((val, i) => (
                          <td key={i}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {invoiceCsvUploadData.length > 10 && (
                  <p>Showing 10 of {invoiceCsvUploadData.length} rows</p>
                )}
              </>
            )}

            <div className="popup-actions">
              <button className="save-pop-up" onClick={submitInvoiceCsv}>
                Submit
              </button>
              <button
                className="cancel-pop-up"
                onClick={() => setShowInvoiceCsvPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* =================== ESTIMATE: CSV Preview + Submit =================== */}
      {showEstimateCsvPopup && (
        <>
          <div
            className="overlay-invoice"
            onClick={() => setShowEstimateCsvPopup(false)}
          ></div>
          <div className="popup-transactions">
            <h4>Upload Estimate CSV</h4>

            <select
              value={uploadEstimateVendor}
              onChange={(e) => setUploadEstimateVendor(e.target.value)}
            >
              <option value="">Select Customer</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>

            <select
              value={uploadEstimateType}
              onChange={(e) => setUploadEstimateType(e.target.value)}
            >
              <option value="one-time">One Time</option>
              <option value="open">Open</option>
            </select>

            {uploadEstimateType === "one-time" && (
              <input
                type="date"
                value={uploadEstimateDate}
                onChange={(e) => setUploadEstimateDate(e.target.value)}
              />
            )}

            {estimateCsvUploadData.length > 0 && (
              <>
                <table className="table transactions-table" style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      {Object.keys(estimateCsvUploadData[0] || {}).map((k) => (
                        <th key={k}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {estimateCsvUploadData.slice(0, 10).map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((val, i) => (
                          <td key={i}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {estimateCsvUploadData.length > 10 && (
                  <p>Showing 10 of {estimateCsvUploadData.length} rows</p>
                )}
              </>
            )}

            <div className="popup-actions">
              <button className="save-pop-up" onClick={submitEstimateCsv}>
                Submit
              </button>
              <button
                className="cancel-pop-up"
                onClick={() => setShowEstimateCsvPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}





// import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./ViewPayroll.css";               // tabs/cards
// import "./InvoiceReferencePage.css";      // reuse popup/table styles

// import InvoiceReferencePage from "./InvoiceReferencePage";
// import InvoicePage from "./InvoicePage";
// import EstimateReferencePage from "./EstimateReferencePage";

// /* ---- SVG Tabs (same as ViewPayroll) ---- */
// function TabSvgDefs() {
//   return (
//     <svg width="0" height="0" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
//       <defs>
//         <filter id="tabShadow">
//           <feComponentTransfer in="SourceGraphic">
//             <feFuncR type="discrete" tableValues="0" />
//             <feFuncG type="discrete" tableValues="0" />
//             <feFuncB type="discrete" tableValues="0" />
//           </feComponentTransfer>
//           <feGaussianBlur stdDeviation="1" />
//           <feComponentTransfer>
//             <feFuncA type="linear" slope="0.2" />
//           </feComponentTransfer>
//           <feOffset dx="5" dy="1" result="shadow" />
//           <feComposite in="SourceGraphic" />
//         </filter>

//         <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
//           <stop offset="0%" stopColor="#E5ECF6" />
//           <stop offset="100%" stopColor="#D7E0ED" />
//         </linearGradient>

//         <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
//           <stop offset="0%" stopColor="#FFFFFF" />
//           <stop offset="100%" stopColor="#FAFBFC" />
//         </linearGradient>

//         <symbol id="tab-shape" viewBox="0 0 122 40">
//           <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
//         </symbol>
//       </defs>
//     </svg>
//   );
// }
// function SvgTabs({ tabs, activeIndex, onChange }) {
//   return (
//     <nav className="svg-tabs" role="navigation" aria-label="Invoice sections">
//       <ul className="svg-tablist">
//         {tabs.map((label, i) => {
//           const active = i === activeIndex;
//           return (
//             <li key={label} className={`svg-tab ${active ? "is-active" : ""}`}>
//               <button
//                 type="button"
//                 className="svg-tab-link"
//                 onClick={() => onChange(i)}
//                 aria-selected={active}
//                 aria-controls={`panel-${i}`}
//                 role="tab"
//               >
//                 <span className="svg-tab-label">{label}</span>
//                 <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
//                   <use xlinkHref="#tab-shape" />
//                 </svg>
//               </button>
//             </li>
//           );
//         })}
//       </ul>
//     </nav>
//   );
// }

// /* ===================== HUB ===================== */
// export default function InvoicesHubPage() {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   /* Tabs */
//   const TAB_KEYS = ["list", "add", "settings", "estimates"];
//   const TAB_LABELS = ["Invoice List", "Add Invoice", "Invoices Setting", "Estimates"];
//   const [activeTab, setActiveTab] = useState("list");
//   const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
//   const setActiveByIndex = (i) => setActiveTab(TAB_KEYS[i] ?? "list");

//   /* Stats + vendors */
//   const [invoices, setInvoices] = useState([]);
//   const [vendors, setVendors] = useState([]);

//   const fetchInvoices = useCallback(async () => {
//     try {
//       const res = await api.get(`/business/${businessId}/invoices`);
//       setInvoices(Array.isArray(res.data) ? res.data : []);
//     } catch (e) {
//       console.error("Failed fetching invoices", e);
//       setInvoices([]);
//     }
//   }, [businessId]);

//   useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

//   useEffect(() => {
//     (async () => {
//       try {
//         const vendorRes = await api.get(`/business/${businessId}/vendors`);
//         setVendors(Array.isArray(vendorRes.data) ? vendorRes.data : []);
//       } catch (e) {
//         console.error("Failed to load vendors", e);
//       }
//     })();
//   }, [businessId]);

//   const stats = useMemo(() => {
//     const total = invoices.length;
//     let paid = 0, pending = 0, overdue = 0;
//     for (const inv of invoices) {
//       const bal = Number(inv.total_balance ?? inv.balance ?? 0);
//       const status = (inv.status || "").toLowerCase();
//       if (status === "paid" || bal <= 0) paid++;
//       else if (status === "overdue") overdue++;
//       else pending++;
//     }
//     return { total, paid, pending, overdue };
//   }, [invoices]);

//   /* -------- CSV helpers (shared) -------- */
//   const parseCsv = (text) => {
//     const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
//     if (!lines.length) return [];
//     const normalize = (h) => {
//       const k = h.trim().toLowerCase();
//       if (k === "date") return "entry_date";
//       if (k === "description") return "description";
//       if (k === "amount") return "amount";
//       return k;
//     };
//     const headers = lines[0].split(",").map((h) => normalize(h));
//     return lines.slice(1).map((line) => {
//       const values = line.split(",").map((v) => v.trim());
//       return headers.reduce((obj, h, i) => {
//         obj[h] = values[i] ?? "";
//         return obj;
//       }, {});
//     });
//   };

//   /* -------- INVOICE CSV (in hub) -------- */
//   const invoiceFileRef = useRef(null);
//   const [showInvoiceCsvPopup, setShowInvoiceCsvPopup] = useState(false);
//   const [invoiceCsvUploadData, setInvoiceCsvUploadData] = useState([]);
//   const [uploadInvoiceType, setUploadInvoiceType] = useState("one-time");
//   const [uploadInvoiceVendor, setUploadInvoiceVendor] = useState("");
//   const [uploadInvoiceDate, setUploadInvoiceDate] = useState("");

//   const handleInvoiceCsvUpload = (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;
//     const reader = new FileReader();
//     reader.onload = (ev) => {
//       setInvoiceCsvUploadData(parseCsv(String(ev.target?.result || "")));
//       setShowInvoiceCsvPopup(true);
//       if (invoiceFileRef.current) invoiceFileRef.current.value = "";
//     };
//     reader.readAsText(file);
//   };

//   const submitInvoiceCsv = async () => {
//     if (!uploadInvoiceVendor || !uploadInvoiceType) return alert("Select customer and type.");
//     if (uploadInvoiceType === "one-time" && !uploadInvoiceDate) return alert("Invoice date is required.");

//     try {
//       await api.post(`/business/${businessId}/invoices/upload`, {
//         invoice_type: uploadInvoiceType,
//         vendor_id: uploadInvoiceVendor,
//         invoice_date: uploadInvoiceType === "one-time" ? uploadInvoiceDate : null,
//         entries: invoiceCsvUploadData,
//       });
//       alert("Invoices uploaded.");
//       setShowInvoiceCsvPopup(false);
//       setInvoiceCsvUploadData([]);
//       setUploadInvoiceVendor("");
//       setUploadInvoiceDate("");
//       await fetchInvoices(); // refresh tiles
//       window.dispatchEvent(new CustomEvent("invoices-refresh")); // tell list tab to reload
//     } catch (err) {
//       console.error("Invoice upload failed", err);
//       alert("Failed to upload invoice CSV.");
//     }
//   };

//   /* -------- ESTIMATE CSV (in hub) -------- */
//   const estimateFileRef = useRef(null);
//   const [showEstimateCsvPopup, setShowEstimateCsvPopup] = useState(false);
//   const [estimateCsvUploadData, setEstimateCsvUploadData] = useState([]);
//   const [uploadEstimateType, setUploadEstimateType] = useState("one-time");
//   const [uploadEstimateVendor, setUploadEstimateVendor] = useState("");
//   const [uploadEstimateDate, setUploadEstimateDate] = useState("");

//   const handleEstimateCsvUpload = (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;
//     const reader = new FileReader();
//     reader.onload = (ev) => {
//       setEstimateCsvUploadData(parseCsv(String(ev.target?.result || "")));
//       setShowEstimateCsvPopup(true);
//       if (estimateFileRef.current) estimateFileRef.current.value = "";
//     };
//     reader.readAsText(file);
//   };

//   const submitEstimateCsv = async () => {
//     if (!uploadEstimateVendor || !uploadEstimateType) return alert("Select customer and type.");
//     if (uploadEstimateType === "one-time" && !uploadEstimateDate) return alert("Estimate date is required.");

//     try {
//       // Try dedicated endpoint first; fallback to invoices/upload with is_estimate flag.
//       try {
//         await api.post(`/business/${businessId}/estimates/upload`, {
//           invoice_type: uploadEstimateType,
//           vendor_id: uploadEstimateVendor,
//           invoice_date: uploadEstimateType === "one-time" ? uploadEstimateDate : null,
//           entries: estimateCsvUploadData,
//         });
//       } catch (e) {
//         await api.post(`/business/${businessId}/invoices/upload`, {
//           is_estimate: 1,
//           invoice_type: uploadEstimateType,
//           vendor_id: uploadEstimateVendor,
//           invoice_date: uploadEstimateType === "one-time" ? uploadEstimateDate : null,
//           entries: estimateCsvUploadData,
//         });
//       }

//       alert("Estimates uploaded.");
//       setShowEstimateCsvPopup(false);
//       setEstimateCsvUploadData([]);
//       setUploadEstimateVendor("");
//       setUploadEstimateDate("");
//       // tell estimate list tab to reload
//       window.dispatchEvent(new CustomEvent("estimates-refresh"));
//     } catch (err) {
//       console.error("Estimate upload failed", err);
//       alert("Failed to upload estimate CSV.");
//     }
//   };

//   /* ===== RENDER ===== */
//   return (
//     <div className="view-payroll-page">
//       <TabSvgDefs />
//       <h1 className="vp-title">Invoice</h1>

//       {/* Stat tiles + context upload buttons */}
//       <div className="vp-charts-row" style={{ alignItems: "stretch" }}>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Total Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.total}</div>
//           <div className="subtext-percent">All invoices created</div>
//         </div>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Paid Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.paid}</div>
//           <div className="subtext-percent">Payments received</div>
//         </div>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Pending Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.pending}</div>
//           <div className="subtext-percent">Awaiting payment</div>
//         </div>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Overdue Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.overdue}</div>
//           <div className="subtext-percent">Past due</div>
//         </div>

//         <div
//   className="vp-chart-card"
//   style={{
//     minWidth: 360,
//     display: "flex",
//     justifyContent: "flex-end",
//     alignItems: "center",
//     gap: 8,
//     flexWrap: "wrap",
//   }}
// >
//   {/* Always-mounted hidden inputs */}
//   <input
//     type="file"
//     accept=".csv"
//     style={{ display: "none" }}
//     ref={invoiceFileRef}
//     onChange={handleInvoiceCsvUpload}
//   />
//   <input
//     type="file"
//     accept=".csv"
//     style={{ display: "none" }}
//     ref={estimateFileRef}
//     onChange={handleEstimateCsvUpload}
//   />

//   {/* Always-visible buttons */}
//   <button
//     className="inv-ref-submit-btn"
//     onClick={() => invoiceFileRef.current?.click()}
//     style={{ background: "#16a34a", color: "#fff", padding: "10px 16px", borderRadius: 10 }}
//   >
//     + Upload Invoice
//   </button>

//   <button
//     className="inv-ref-submit-btn"
//     onClick={() => estimateFileRef.current?.click()}
//     style={{ background: "#16a34a", color: "#fff", padding: "10px 16px", borderRadius: 10 }}
//   >
//     + Upload Estimate
//   </button>
// </div>

//       </div>

//       <SvgTabs tabs={TAB_LABELS} activeIndex={activeTabIndex} onChange={setActiveByIndex} />

//       <div className="svg-panels">
//         {/* Invoice List */}
//         <div id="panel-0" className={`pw-card tab-panel ${activeTab === "list" ? "is-active" : ""}`}>
//           <InvoiceReferencePage />
//         </div>

//         {/* Add Invoice */}
//         <div id="panel-1" className={`pw-card tab-panel ${activeTab === "add" ? "is-active" : ""}`}>
//           <button
//             className="inv-ref-submit-btn"
//             onClick={() => navigate(`/dashboard/business/${businessId}/invoicecreate`)}
//           >
//             + Add Invoice
//           </button>
//         </div>

//         {/* Settings */}
//         <div id="panel-2" className={`pw-card tab-panel ${activeTab === "settings" ? "is-active" : ""}`}>
//           <InvoicePage />
//         </div>

//         {/* Estimates */}
//         <div id="panel-3" className={`pw-card tab-panel ${activeTab === "estimates" ? "is-active" : ""}`}>
//           <EstimateReferencePage />
//         </div>
//       </div>

//       {/* ----------------- INVOICE CSV POPUP ----------------- */}
//       {showInvoiceCsvPopup && (
//         <>
//           <div className="overlay-invoice" onClick={() => setShowInvoiceCsvPopup(false)} />
//           <div className="popup-invoice">
//             <h4>Upload Invoice CSV</h4>

//             <select value={uploadInvoiceVendor} onChange={(e) => setUploadInvoiceVendor(e.target.value)}>
//               <option value="">Select Customer</option>
//               {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
//             </select>

//             <select value={uploadInvoiceType} onChange={(e) => setUploadInvoiceType(e.target.value)}>
//               <option value="one-time">One Time</option>
//               <option value="open">Open</option>
//             </select>

//             {uploadInvoiceType === "one-time" && (
//               <input
//                 type="date"
//                 value={uploadInvoiceDate}
//                 onChange={(e) => setUploadInvoiceDate(e.target.value)}
//               />
//             )}

//             {invoiceCsvUploadData.length > 0 && (
//               <>
//                 <table className="table transactions-table" style={{ marginTop: 10 }}>
//                   <thead>
//                     <tr>
//                       {Object.keys(invoiceCsvUploadData[0] || {}).map((k) => <th key={k}>{k}</th>)}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {invoiceCsvUploadData.slice(0, 10).map((row, idx) => (
//                       <tr key={idx}>
//                         {Object.values(row).map((val, i) => <td key={i}>{val}</td>)}
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//                 {invoiceCsvUploadData.length > 10 && <p>Showing 10 of {invoiceCsvUploadData.length} rows</p>}
//               </>
//             )}

//             <div className="popup-actions">
//               <button className="save-pop-up" onClick={submitInvoiceCsv}>Submit</button>
//               <button className="cancel-pop-up" onClick={() => setShowInvoiceCsvPopup(false)}>Cancel</button>
//             </div>
//           </div>
//         </>
//       )}

//       {/* ----------------- ESTIMATE CSV POPUP ----------------- */}
//       {showEstimateCsvPopup && (
//         <>
//           <div className="overlay-invoice" onClick={() => setShowEstimateCsvPopup(false)} />
//           <div className="popup-invoice">
//             <h4>Upload Estimate CSV</h4>

//             <select value={uploadEstimateVendor} onChange={(e) => setUploadEstimateVendor(e.target.value)}>
//               <option value="">Select Customer</option>
//               {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
//             </select>

//             <select value={uploadEstimateType} onChange={(e) => setUploadEstimateType(e.target.value)}>
//               <option value="one-time">One Time</option>
//               <option value="open">Open</option>
//             </select>

//             {uploadEstimateType === "one-time" && (
//               <input
//                 type="date"
//                 value={uploadEstimateDate}
//                 onChange={(e) => setUploadEstimateDate(e.target.value)}
//               />
//             )}

//             {estimateCsvUploadData.length > 0 && (
//               <>
//                 <table className="table transactions-table" style={{ marginTop: 10 }}>
//                   <thead>
//                     <tr>
//                       {Object.keys(estimateCsvUploadData[0] || {}).map((k) => <th key={k}>{k}</th>)}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {estimateCsvUploadData.slice(0, 10).map((row, idx) => (
//                       <tr key={idx}>
//                         {Object.values(row).map((val, i) => <td key={i}>{val}</td>)}
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//                 {estimateCsvUploadData.length > 10 && <p>Showing 10 of {estimateCsvUploadData.length} rows</p>}
//               </>
//             )}

//             <div className="popup-actions">
//               <button className="save-pop-up" onClick={submitEstimateCsv}>Submit</button>
//               <button className="cancel-pop-up" onClick={() => setShowEstimateCsvPopup(false)}>Cancel</button>
//             </div>
//           </div>
//         </>
//       )}
//     </div>
//   );
// }






// // src/components/InvoicesHubPage.jsx
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./ViewPayroll.css"; // reuse tab/card styles

// // bring these to the TOP (fixes import/first)
// import InvoiceReferencePage from "./InvoiceReferencePage";
// import InvoicePage from "./InvoicePage"; // settings
// import EstimateReferencePage from "./EstimateReferencePage";



// function TabSvgDefs() {
//   return (
//     <svg width="0" height="0" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
//       <defs>
//         <filter id="tabShadow">
//           <feComponentTransfer in="SourceGraphic">
//             <feFuncR type="discrete" tableValues="0" />
//             <feFuncG type="discrete" tableValues="0" />
//             <feFuncB type="discrete" tableValues="0" />
//           </feComponentTransfer>
//           <feGaussianBlur stdDeviation="1" />
//           <feComponentTransfer>
//             <feFuncA type="linear" slope="0.2" />
//           </feComponentTransfer>
//           <feOffset dx="5" dy="1" result="shadow" />
//           <feComposite in="SourceGraphic" />
//         </filter>

//         <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
//           <stop offset="0%" stopColor="#E5ECF6" />
//           <stop offset="100%" stopColor="#D7E0ED" />
//         </linearGradient>

//         <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
//           <stop offset="0%" stopColor="#FFFFFF" />
//           <stop offset="100%" stopColor="#FAFBFC" />
//         </linearGradient>

//         <symbol id="tab-shape" viewBox="0 0 122 40">
//           <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
//         </symbol>
//       </defs>
//     </svg>
//   );
// }

// function SvgTabs({ tabs, activeIndex, onChange }) {
//   return (
//     <nav className="svg-tabs" role="navigation" aria-label="Invoice sections">
//       <ul className="svg-tablist">
//         {tabs.map((label, i) => {
//           const active = i === activeIndex;
//           return (
//             <li key={label} className={`svg-tab ${active ? "is-active" : ""}`}>
//               <button
//                 type="button"
//                 className="svg-tab-link"
//                 onClick={() => onChange(i)}
//                 aria-selected={active}
//                 aria-controls={`panel-${i}`}
//                 role="tab"
//               >
//                 <span className="svg-tab-label">{label}</span>
//                 <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
//                   <use xlinkHref="#tab-shape" />
//                 </svg>
//               </button>
//             </li>
//           );
//         })}
//       </ul>
//     </nav>
//   );
// }

// export default function InvoicesHubPage() {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   const TAB_KEYS = ["list", "add", "settings", "estimates"];
//   const TAB_LABELS = ["Invoice List", "Add Invoice", "Invoices Setting", "Estimates"];
//   const [activeTab, setActiveTab] = useState("list");
//   const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
//   const setActiveByIndex = (i) => setActiveTab(TAB_KEYS[i] ?? "list");

//   const [invoices, setInvoices] = useState([]);
//   const fetchInvoices = useCallback(async () => {
//     try {
//       const res = await api.get(`/business/${businessId}/invoices`);
//       setInvoices(Array.isArray(res.data) ? res.data : []);
//     } catch (e) {
//       console.error("Failed fetching invoices", e);
//       setInvoices([]);
//     }
//   }, [businessId]);

//   useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

//   const stats = useMemo(() => {
//     const total = invoices.length;
//     let paid = 0, pending = 0, overdue = 0;
//     for (const inv of invoices) {
//       const bal = Number(inv.total_balance ?? inv.balance ?? 0);
//       const status = (inv.status || "").toLowerCase();
//       if (status === "paid" || bal <= 0) paid++;
//       else if (status === "overdue") overdue++;
//       else pending++;
//     }
//     return { total, paid, pending, overdue };
//   }, [invoices]);

//   return (
//     <div className="view-payroll-page">
//       <TabSvgDefs />
//       <h1 className="vp-title">Invoice</h1>

//       <div className="pw-stats-grid" style={{ alignItems: "stretch" }}>
        
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Total Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.total}</div>
//           <div className="subtext-percent">All invoices created</div>
//         </div>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Paid Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.paid}</div>
//           <div className="subtext-percent">Payments received</div>
//         </div>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Pending Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.pending}</div>
//           <div className="subtext-percent">Awaiting payment</div>
//         </div>
//         <div className="vp-chart-card" style={{ minWidth: 220 }}>
//           <div className="vp-chart-title">Overdue Invoices</div>
//           <div style={{ fontSize: 40, fontWeight: 700 }}>{stats.overdue}</div>
//           <div className="subtext-percent">Past due</div>
//         </div>

//         <div className="vp-chart-card" style={{ minWidth: 220, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
//           <button
//             className="inv-ref-submit-btn"
//             onClick={() => window.dispatchEvent(new CustomEvent("open-invoice-upload"))}
//             style={{ background: "#16a34a", color: "#fff", padding: "10px 16px", borderRadius: 10 }}
//           >
//             + Upload Invoice
//           </button>
//         </div>
//       </div>

//       <SvgTabs tabs={TAB_LABELS} activeIndex={activeTabIndex} onChange={setActiveByIndex} />

//       <div className="svg-panels">
//         <div id="panel-0" className={`pw-card tab-panel ${activeTab === "list" ? "is-active" : ""}`}>
//           <InvoiceReferencePage />
//         </div>

//         <div id="panel-1" className={`pw-card tab-panel ${activeTab === "add" ? "is-active" : ""}`}>
//           <button
//             className="inv-ref-submit-btn"
//             onClick={() => navigate(`/dashboard/business/${businessId}/invoicecreate`)}
//           >
//             + Add Invoice
//           </button>
//           <button className="add-est-btn" onClick={() => navigate(`/dashboard/business/${businessId}/estimatecreate`)}>
//         New Estimate
//         </button>
//         </div>

//         <div id="panel-2" className={`pw-card tab-panel ${activeTab === "settings" ? "is-active" : ""}`}>
//           <InvoicePage />
//         </div>

//         <div id="panel-3" className={`pw-card tab-panel ${activeTab === "estimates" ? "is-active" : ""}`}>
//           <EstimateReferencePage />
//         </div>
//       </div>
//     </div>
//   );
// }
